import { useEffect, useState } from "react";
import { useDrag } from "react-use-gesture";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import {
  helpContentState,
  helpFixState,
  helpPosState,
  openHelpState,
} from "../recoil";
import { Box, Button, IconButton, Typography } from "@mui/material";
import { DragIndicatorIcon, PushPinIcon, OpenInNewIcon } from "../icons";
import { t } from "i18next";
import HelpWindow from "./HelpWindow";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  position: { x: number; y: number };
  setModalPos: (pos: { x: number; y: number }) => void;
  onOpenWindow: () => void;
}

function Modal({
  isOpen,
  onClose,
  children,
  position,
  setModalPos,
  onOpenWindow,
}: ModalProps) {
  const setHelpFix = useSetRecoilState(helpFixState);

  const resetBodyStyle = () => {
    document.body.style.width = "";
    document.body.style.overflowX = "";
  };

  const bind = useDrag(({ offset }) => {
    const newX = offset[0];
    const newY = offset[1];

    const boundedY = Math.max(0, newY);

    if (newX < 0) {
      const requiredWidth = window.innerWidth + Math.abs(newX);
      document.body.style.width = `${requiredWidth}px`;
      document.body.style.overflowX = "auto";
      setModalPos({ x: 0, y: boundedY });
    } else {
      resetBodyStyle();
      setModalPos({ x: newX, y: boundedY });
    }
  });

  const handleFix = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    resetBodyStyle();
    setHelpFix((prev) => !prev);
    onClose();
  };

  const handleClose = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    resetBodyStyle();
    onClose();
  };

  const handleOpenHelpWindow = () => {
    resetBodyStyle();
    onOpenWindow();
  };

  useEffect(() => {
    return () => {
      resetBodyStyle();
    };
  }, []);

  if (!isOpen) return null;

  return (
    <>
      <Box
        id="help-box"
        sx={{
          position: "absolute",
          top: `${Math.max(0, position.y)}px`,
          left: `${position.x}px`,
          backgroundColor: "var(--dk-surface, #fff)",
          padding: "20px",
          borderRadius: "8px",
          border: "1px solid var(--dk-border, rgba(0, 0, 0, 0.12))",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          zIndex: 1000,
          width: "300px",
          minWidth: "300px",
          maxWidth: "300px",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box style={{ display: "flex", alignItems: "center" }}>
          <IconButton
            {...bind()}
            style={{
              cursor: "move",
              borderRadius: "4px",
              width: "fit-content",
            }}
          >
            <DragIndicatorIcon />
          </IconButton>
          <IconButton
            style={{
              cursor: "pointer",
              borderRadius: "4px",
              width: "fit-content",
            }}
            onClick={handleFix}
          >
            <PushPinIcon />
          </IconButton>
          <IconButton
            style={{
              cursor: "pointer",
              borderRadius: "4px",
              width: "fit-content",
            }}
            onClick={handleOpenHelpWindow}
            title="독립 창으로 열기"
          >
            <OpenInNewIcon />
          </IconButton>
          <Typography sx={{ color: "var(--text-color)" }}>
            {t("Help")}
          </Typography>
        </Box>
        <Box sx={{ height: "200px" }}>{children}</Box>
        <Button
          id="help-close"
          variant="contained"
          onClick={handleClose}
          sx={{ display: "flex", justifyContent: "flex-end" }}
        >
          Close
        </Button>
      </Box>
    </>
  );
}

export default function HelpModeless() {
  const [helpPos, setHelpPos] = useRecoilState(helpPosState);
  const [openHelp, setOpenHelp] = useRecoilState(openHelpState);
  const [showHelpWindow, setShowHelpWindow] = useState(false);
  const helpContent = useRecoilValue(helpContentState);

  const closeModal = () => {
    setOpenHelp(false);
  };

  const handleOpenHelpWindow = () => {
    setShowHelpWindow(true);
    closeModal();
  };

  const handleCloseHelpWindow = () => {
    setShowHelpWindow(false);
  };

  useEffect(() => {
    if (!showHelpWindow && openHelp) {
      return;
    }
  }, [showHelpWindow, openHelp]);

  return (
    <>
      <Modal
        isOpen={openHelp}
        onClose={closeModal}
        position={helpPos}
        setModalPos={setHelpPos}
        onOpenWindow={handleOpenHelpWindow}
      >
        <Typography variant="body1">{helpContent}</Typography>
      </Modal>
      {showHelpWindow && (
        <HelpWindow isOpen={true} onClose={handleCloseHelpWindow} />
      )}
    </>
  );
}
